/**
 * One financial goal. Everything below the fields is derived locally as the
 * user types — segment from the thumb rule, cost inflated to the goal date, and
 * the instalment that alone would close it.
 */

import React from "react";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { rupee, yearsLabel } from "../../../Helpers/portfolioPlanner/format";
import type {
  PlannerConfig,
  PlannerGoalType,
  PlannerOptions,
} from "../../../Helpers/portfolioPlanner/types";
import {
  BadgeRow,
  DateField,
  MoneyField,
  NumberField,
  SelectField,
  TextField,
  WholeNumberField,
} from "./PlannerFields";
import styles from "./portfolio-planner.module.css";
import type { GoalBadges, GoalDraft } from "./usePlannerForm";

interface PlannerGoalRowProps {
  goal: GoalDraft;
  index: number;
  badges: GoalBadges;
  options: PlannerOptions;
  config: PlannerConfig;
  today: string;
  canRemove: boolean;
  onPatch: (patch: Partial<GoalDraft>) => void;
  onRemove: () => void;
}

const PlannerGoalRow = ({
  goal,
  index,
  badges,
  options,
  config,
  today,
  canRemove,
  onPatch,
  onRemove,
}: PlannerGoalRowProps) => {
  const liveBadges: { text: string; tone?: "neutral" | "good" | "warn" | "accent" }[] =
    [];

  if (badges.segment) {
    liveBadges.push({ text: badges.segmentLabel, tone: "accent" });
  }
  if (badges.tenureYears > 0) {
    liveBadges.push({ text: `Horizon ${yearsLabel(badges.tenureYears)}` });
  }
  if (badges.futureCost != null) {
    liveBadges.push({ text: `Cost at target ${rupee(badges.futureCost)}` });
  }
  if (badges.indicativeSip != null) {
    liveBadges.push({
      text: `≈ ${rupee(badges.indicativeSip)} / ${goal.sipFrequency.toLowerCase()} on its own`,
    });
  }
  if (!badges.isComplete) {
    liveBadges.push({
      text: "Needs a cost and a timeline to be included",
      tone: "warn",
    });
  }

  return (
    <div className={styles.rowCard}>
      <div className={styles.rowHead}>
        <span className={styles.rowTitle}>
          <i className={styles.rowIndex}>{index + 1}</i>
          {goal.name.trim() || goal.type}
        </span>
        {canRemove ? (
          <button type="button" className={styles.rowRemove} onClick={onRemove}>
            <DeleteOutlineIcon style={{ fontSize: 14 }} aria-hidden="true" />
            Remove
          </button>
        ) : null}
      </div>

      <div className={styles.grid}>
        <SelectField<PlannerGoalType>
          label="Goal type"
          value={goal.type}
          options={options.goalType}
          onChange={(value) => onPatch({ type: value })}
        />
        <TextField
          label="Goal name"
          value={goal.name}
          placeholder="e.g. Riya's college"
          onChange={(value) => onPatch({ name: value })}
        />
        <DateField
          label="Goal start date"
          value={goal.startDate}
          min={today}
          onChange={(value) => onPatch({ startDate: value })}
        />
        <WholeNumberField
          label="Timeline — years"
          value={goal.years}
          placeholder="e.g. 6"
          maxLength={2}
          suffix="yr"
          onChange={(value) => onPatch({ years: value })}
        />
        <WholeNumberField
          label="Timeline — months"
          value={goal.months}
          placeholder="0–11"
          suffix="mo"
          hint="12 or more rolls into years."
          onChange={(value) => onPatch({ months: value })}
        />
        <MoneyField
          label="Cost of goal today"
          value={goal.costToday}
          placeholder="25,00,000"
          onChange={(value) => onPatch({ costToday: value })}
        />
        <NumberField
          label="Expected return"
          value={goal.expectedReturn}
          // Prefilled from the segment until touched, then left alone for good.
          tag={goal.returnTouched ? undefined : "auto"}
          placeholder={
            badges.segment ? String(config.defaultReturn[badges.segment]) : "auto"
          }
          step={0.1}
          suffix="%"
          hint={
            goal.returnTouched
              ? "Your figure — the segment prefill no longer applies."
              : "Set from the goal's segment. Type over it to use your own."
          }
          onChange={(value) => onPatch({ expectedReturn: value })}
        />
        <NumberField
          label="Inflation"
          value={goal.inflation}
          placeholder={String(config.inflationDefault)}
          step={0.1}
          suffix="%"
          onChange={(value) => onPatch({ inflation: value })}
        />
      </div>

      <BadgeRow badges={liveBadges} />
    </div>
  );
};

export default PlannerGoalRow;
